import { useState } from 'react'
import './PatientSearchFilters.css'

export interface PatientFilters {
  search: string
  gender?: string
  status?: string
  dateOfBirthFrom?: string
  dateOfBirthTo?: string
  hasAllergies?: boolean
  hasInsurance?: boolean
}

interface PatientSearchFiltersProps {
  filters: PatientFilters
  onFiltersChange: (filters: PatientFilters) => void
  onClear: () => void
  onSearch?: () => void
  isLoading?: boolean
  hasSearched?: boolean
  pageSize?: number
  onPageSizeChange?: (pageSize: number) => void
}

export function PatientSearchFilters({
  filters,
  onFiltersChange,
  onClear,
  onSearch,
  isLoading = false,
  hasSearched: _hasSearched = false,
  pageSize,
  onPageSizeChange,
}: PatientSearchFiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const handleFilterChange = (key: keyof PatientFilters, value: any) => {
    onFiltersChange({
      ...filters,
      [key]: value || undefined,
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSearch) {
      e.preventDefault()
      onSearch()
    }
  }

  const hasSearchCriteria = Boolean(
    filters.search?.trim() ||
    filters.gender ||
    filters.status ||
    filters.dateOfBirthFrom ||
    filters.dateOfBirthTo ||
    filters.hasAllergies !== undefined ||
    filters.hasInsurance !== undefined
  )

  const hasActiveFilters = Boolean(
    filters.gender ||
    filters.status ||
    filters.dateOfBirthFrom ||
    filters.dateOfBirthTo ||
    filters.hasAllergies !== undefined ||
    filters.hasInsurance !== undefined
  )

  return (
    <div className="patient-search-filters">
      <div className="search-bar">
        <div className="search-input-wrapper">
          <input
            type="text"
            placeholder="Search by name, email, phone, patient number, PPS, or GMS..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            onKeyDown={handleKeyDown}
            className="search-input"
          />
          {filters.search && (
            <button
              className="clear-search"
              onClick={() => handleFilterChange('search', '')}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
        {onSearch && (
          <button
            type="button"
            className="search-button"
            onClick={onSearch}
            disabled={isLoading || !hasSearchCriteria}
            title="Search for patients"
          >
            {isLoading ? (
              <svg className="spinner" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2" strokeDasharray="43.98" strokeDashoffset="10" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M7 13A6 6 0 1 0 7 1a6 6 0 0 0 0 12zM15 15l-4.35-4.35"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            Search
          </button>
        )}
        {onPageSizeChange && (
          <div className="page-size-selector">
            <label htmlFor="patient-page-size">Page size</label>
            <select
              id="patient-page-size"
              value={pageSize || 50}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        )}
        <button
          className={`filter-toggle ${hasActiveFilters ? 'active' : ''}`}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M2 4h12M4 8h8M6 12h4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          Filters
          {hasActiveFilters && <span className="filter-badge">{Object.keys(filters).filter(k => k !== 'search' && filters[k as keyof PatientFilters]).length}</span>}
        </button>
      </div>

      {isExpanded && (
        <div className="filter-panel">
          <div className="filter-row">
            <div className="filter-group">
              <label>Gender</label>
              <select
                value={filters.gender || ''}
                onChange={(e) => handleFilterChange('gender', e.target.value)}
              >
                <option value="">All</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Status</label>
              <select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Date of Birth (From)</label>
              <input
                type="date"
                value={filters.dateOfBirthFrom || ''}
                onChange={(e) => handleFilterChange('dateOfBirthFrom', e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label>Date of Birth (To)</label>
              <input
                type="date"
                value={filters.dateOfBirthTo || ''}
                onChange={(e) => handleFilterChange('dateOfBirthTo', e.target.value)}
              />
            </div>
          </div>

          <div className="filter-row">
            <div className="filter-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={filters.hasAllergies === true}
                  onChange={(e) =>
                    handleFilterChange('hasAllergies', e.target.checked ? true : undefined)
                  }
                />
                Has Allergies
              </label>
            </div>

            <div className="filter-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={filters.hasInsurance === true}
                  onChange={(e) =>
                    handleFilterChange('hasInsurance', e.target.checked ? true : undefined)
                  }
                />
                Has Insurance
              </label>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="filter-actions">
              <button className="btn-clear-filters" onClick={onClear}>
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

